/**
 * CategorizedSymbologyPanel
 *
 * UI component for configuring categorized (unique-value) symbology.
 * Allows users to select an attribute field, view unique values,
 * assign custom colors per category, and group categories.
 *
 * **Validates: Requirements 41.1, 41.2, 41.4, 41.6**
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  TextField,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Paper,
} from '@mui/material';
import { Palette, GroupWork, Refresh } from '@mui/icons-material';
import type { CategorizedRule, SymbolStyle, CategoryGroup } from '../types/symbology';
import { generateCategoryColors } from '../services/SymbologyEngine';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CategorizedSymbologyPanelProps {
  /** Available attribute field names. */
  fields: string[];
  /** Features with their properties for extracting unique values. */
  features: Array<{ properties: Record<string, any> }>;
  /** Currently selected field (if any). */
  selectedField?: string;
  /** Current categorized rules (if any). */
  currentRules?: CategorizedRule[];
  /** Current category groups (if any). */
  currentGroups?: CategoryGroup[];
  /** Called when the user applies the categorized symbology. */
  onApply: (field: string, rules: CategorizedRule[], groups?: CategoryGroup[]) => void;
  /** Called when the user cancels. */
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Helper: Color input
// ---------------------------------------------------------------------------

const isValidHex = (color: string) => /^#[0-9A-Fa-f]{6}$/.test(color);

interface CategoryColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  label: string;
}

const CategoryColorPicker: React.FC<CategoryColorPickerProps> = ({ color, onChange, label }) => (
  <Tooltip title={`Change color for ${label}`}>
    <Box
      component="label"
      sx={{
        width: 28,
        height: 28,
        borderRadius: '4px',
        border: '2px solid',
        borderColor: 'divider',
        backgroundColor: isValidHex(color) ? color : '#cccccc',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <input
        type="color"
        value={isValidHex(color) ? color : '#cccccc'}
        onChange={(e) => onChange(e.target.value)}
        style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
        aria-label={`Color picker for ${label}`}
      />
    </Box>
  </Tooltip>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CategorizedSymbologyPanel: React.FC<CategorizedSymbologyPanelProps> = ({
  fields,
  features,
  selectedField: initialField,
  currentRules,
  currentGroups,
  onApply,
  onCancel,
}) => {
  const [selectedField, setSelectedField] = useState<string>(initialField || '');
  const [rules, setRules] = useState<CategorizedRule[]>(currentRules || []);
  const [groups, setGroups] = useState<CategoryGroup[]>(currentGroups || []);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedForGroup, setSelectedForGroup] = useState<Set<string>>(new Set());

  // Extract unique values when field changes
  const uniqueValues = useMemo(() => {
    if (!selectedField) return [];
    const seen = new Set<string>();
    const values: unknown[] = [];
    for (const feature of features) {
      const val = feature.properties?.[selectedField];
      if (val !== undefined && val !== null) {
        const key = String(val);
        if (!seen.has(key)) {
          seen.add(key);
          values.push(val);
        }
      }
    }
    return values;
  }, [features, selectedField]);

  // Generate rules when field changes
  useEffect(() => {
    if (
      selectedField &&
      uniqueValues.length > 0 &&
      (!currentRules || currentRules[0]?.attributeField !== selectedField)
    ) {
      const colors = generateCategoryColors(uniqueValues.length);
      const newRules: CategorizedRule[] = uniqueValues.map((value, index) => ({
        attributeField: selectedField,
        value,
        symbol: {
          fillColor: colors[index],
          strokeColor: '#ffffff',
          strokeWidth: 1,
          opacity: 1,
        },
        label: String(value),
      }));
      setRules(newRules);
      setGroups([]);
    }
  }, [selectedField, uniqueValues, currentRules]);

  // Handle color change for a specific category
  const handleColorChange = useCallback((index: number, color: string) => {
    setRules((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        symbol: { ...updated[index].symbol, fillColor: color },
      };
      return updated;
    });
  }, []);

  // Randomize colors
  const handleRandomizeColors = useCallback(() => {
    const colors = generateCategoryColors(rules.length);
    setRules((prev) =>
      prev.map((rule, i) => ({
        ...rule,
        symbol: { ...rule.symbol, fillColor: colors[i] },
      }))
    );
  }, [rules.length]);

  // Toggle selection for grouping
  const toggleGroupSelection = useCallback((value: string) => {
    setSelectedForGroup((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  }, []);

  // Create a group from selected values
  const handleCreateGroup = useCallback(() => {
    if (groupName && selectedForGroup.size > 1) {
      const groupValues = rules
        .filter((r) => selectedForGroup.has(String(r.value)))
        .map((r) => r.value);
      const firstRule = rules.find((r) => selectedForGroup.has(String(r.value)));
      const newGroup: CategoryGroup = {
        name: groupName,
        values: groupValues,
        symbol: firstRule?.symbol || {
          fillColor: '#3388ff',
          strokeColor: '#ffffff',
          strokeWidth: 1,
          opacity: 1,
        },
      };
      setGroups((prev) => [...prev, newGroup]);
      setGroupDialogOpen(false);
      setGroupName('');
      setSelectedForGroup(new Set());
    }
  }, [groupName, selectedForGroup, rules]);

  // Remove a group
  const handleRemoveGroup = useCallback((index: number) => {
    setGroups((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Apply
  const handleApply = useCallback(() => {
    if (selectedField && rules.length > 0) {
      onApply(selectedField, rules, groups.length > 0 ? groups : undefined);
    }
  }, [selectedField, rules, groups, onApply]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Palette color="primary" fontSize="small" />
        Categorized Symbology
      </Typography>

      {/* Field selector */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="categorized-field-label">Attribute Field</InputLabel>
        <Select
          labelId="categorized-field-label"
          value={selectedField}
          label="Attribute Field"
          onChange={(e) => setSelectedField(e.target.value)}
          inputProps={{ 'aria-label': 'Select attribute field for categorization' }}
        >
          {fields.map((field) => (
            <MenuItem key={field} value={field}>
              {field}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Category list */}
      {rules.length > 0 && (
        <>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Categories ({rules.length})
            </Typography>
            <Box>
              <Tooltip title="Randomize colors">
                <IconButton
                  size="small"
                  onClick={handleRandomizeColors}
                  aria-label="Randomize colors"
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Group selected categories">
                <IconButton
                  size="small"
                  onClick={() => setGroupDialogOpen(true)}
                  disabled={selectedForGroup.size < 2}
                  aria-label="Group categories"
                >
                  <GroupWork fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Groups display */}
          {groups.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Groups
              </Typography>
              {groups.map((group, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <CategoryColorPicker
                    color={group.symbol.fillColor || '#3388ff'}
                    onChange={(color) => {
                      setGroups((prev) => {
                        const updated = [...prev];
                        updated[idx] = {
                          ...updated[idx],
                          symbol: { ...updated[idx].symbol, fillColor: color },
                        };
                        return updated;
                      });
                    }}
                    label={group.name}
                  />
                  <Typography variant="body2">{group.name}</Typography>
                  <Chip label={`${group.values.length} values`} size="small" variant="outlined" />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveGroup(idx)}
                    aria-label={`Remove group ${group.name}`}
                  >
                    ×
                  </IconButton>
                </Box>
              ))}
            </Paper>
          )}

          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {rules.map((rule, index) => {
              const valueStr = String(rule.value);
              const isInGroup = groups.some((g) => g.values.some((v) => String(v) === valueStr));
              const isSelected = selectedForGroup.has(valueStr);

              return (
                <ListItem
                  key={valueStr}
                  sx={{
                    py: 0.5,
                    opacity: isInGroup ? 0.5 : 1,
                    backgroundColor: isSelected ? 'action.selected' : 'transparent',
                    borderRadius: 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => !isInGroup && toggleGroupSelection(valueStr)}
                >
                  <CategoryColorPicker
                    color={rule.symbol.fillColor || '#3388ff'}
                    onChange={(color) => handleColorChange(index, color)}
                    label={valueStr}
                  />
                  <ListItemText
                    primary={valueStr}
                    sx={{ ml: 1 }}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                  {isInGroup && (
                    <Chip label="Grouped" size="small" variant="outlined" color="info" />
                  )}
                </ListItem>
              );
            })}
          </List>
        </>
      )}

      {selectedField && rules.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No unique values found for field "{selectedField}".
        </Typography>
      )}

      {/* Actions */}
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={!selectedField || rules.length === 0}
        >
          Apply
        </Button>
      </Stack>

      {/* Group creation dialog */}
      <Dialog
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create Category Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            size="small"
            sx={{ mt: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Selected values: {Array.from(selectedForGroup).join(', ')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateGroup} variant="contained" disabled={!groupName}>
            Create Group
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategorizedSymbologyPanel;
